import { useEffect, useReducer, useRef } from 'react'

import { GENERATION_PHASE } from 'Pods/ThreeDStudio/Store/constants'

import phaseAnchorMs from './phaseAnchorMs'

// Hardcoded per-phase fill per product decision (no real progress data exists);
// generating eases from its base towards GENERATING_END across the run's estimate
// so the bar keeps moving during the longest stage.
const PHASE_PERCENT = {
  [GENERATION_PHASE.QUEUED]: 8,
  [GENERATION_PHASE.ANALYZING]: 20,
  [GENERATION_PHASE.GENERATING]: 30,
  [GENERATION_PHASE.REFINING]: 85,
  [GENERATION_PHASE.FINALIZING]: 95,
}
const GENERATING_END = 75
// The run's own estimate is what the bar eases across, so a 20s image fills as fast as it
// finishes and a 9-minute hero run keeps moving for all nine. Only runs with no estimate at all
// (a reconnect that never received one) fall back to a flat ramp.
const FALLBACK_RAMP_MS = 3 * 60 * 1000
export const GENERATING_TICK_MS = 1000

const percentFor = (phase, run) => {
  const base = PHASE_PERCENT[phase] ?? PHASE_PERCENT[GENERATION_PHASE.QUEUED]
  if (phase !== GENERATION_PHASE.GENERATING) {
    return base
  }

  const elapsed = Math.max(Date.now() - phaseAnchorMs(run), 0)
  const rampMs = run?.estimatedSeconds != null ? run.estimatedSeconds * 1000 : FALLBACK_RAMP_MS
  return base + (GENERATING_END - base) * Math.min(elapsed / rampMs, 1)
}

/**
 * The progress bar fill (0-100) for the displayed phase. Ticks while generating so
 * the time-based easing advances; never moves backwards, even when the phase source
 * momentarily does (hold, reconcile). Resets when the watched run changes.
 * @param {string} phase - The displayed GENERATION_PHASE value
 * @param {object|undefined} run - The watched activeRuns entry
 * @returns {number}
 */
export default function useProgressPercent(phase, run) {
  const [, tick] = useReducer((count) => count + 1, 0)
  const runId = run?.runId ?? null
  const maxPercentRef = useRef(0)
  const runIdRef = useRef(runId)

  useEffect(() => {
    if (phase !== GENERATION_PHASE.GENERATING) {
      return undefined
    }

    const intervalId = setInterval(tick, GENERATING_TICK_MS)
    return () => clearInterval(intervalId)
  }, [phase])

  // With concurrent generations the watched run is swapped under one mounted card,
  // so the ceiling has to drop back or the queued run inherits the previous run's
  // 95%.
  if (runIdRef.current !== runId) {
    runIdRef.current = runId
    maxPercentRef.current = 0
  }

  maxPercentRef.current = Math.max(maxPercentRef.current, percentFor(phase, run))
  return maxPercentRef.current
}
